"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wind, Wrench, Layers, ArrowRight, MapPin, Building2, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const sitePhotoQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 50
});
function HvacSitePhoto() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const photo = posts.find(p => p.title === "HVAC Contractors Access Image" || p.customFields?.photo_name === "HVAC Contractors Access Image");
  const imgSrc = photo?.customFields?.image || photo?.featuredImage;
  const altText = photo?.customFields?.alt_text || "Rooftop scaffolding setup for commercial HVAC maintenance";
  if (imgSrc) {
    const dynamicImage = {
      src: imgSrc,
      srcset: "",
      sizes: ""
    };
    return <img src={dynamicImage.src} data-wvc-srcset={dynamicImage.srcset} data-wvc-sizes={dynamicImage.sizes} alt={altText} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hvac_plant_scaffolding_access.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hvac_plant_scaffolding_access.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/hvac_plant_scaffolding_access.png-wvc-sizes" alt="Rooftop scaffolding setup for commercial HVAC maintenance" loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
const accessSolutions = [{
  title: "Rooftop plant & chillers",
  description: "Freestanding towers, edge protection, and hop-up platforms designed to service heavy air handling units without puncturing membrane roofs.",
  iconName: "Wind"
}, {
  title: "External condenser brackets",
  description: "Cantilevered and perimeter access platforms positioned precisely for mounting and wiring exterior HVAC condenser units at height.",
  iconName: "Layers"
}, {
  title: "Mechanical riser shafts",
  description: "Internal shaft scaffolding and heavy-duty staging built to support ductwork runs, pipe installation, and routine maintenance.",
  iconName: "Wrench"
}];
const serviceAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "Greater London", "South East"];
const standardAssurances = ["NASC, CHAS and Constructionline accredited", "TG20:21 compliant designs & site-specific method statements", "Out-of-hours & weekend erection to minimise plant downtime"];
export default function HvacScaffolding() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="hvacplantmaintenancescaffolding" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Main Content Column */}
          <motion.div className="lg:col-span-7 flex flex-col items-start" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants}>
            <motion.div variants={itemVariants}>
              <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider border-border bg-card">
                Specialist Plant & HVAC Access
              </Badge>
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              Scaffolding for HVAC and rooftop plant maintenance
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Safe, engineered working platforms for air conditioning installation, external condenser replacements, and mechanical riser access across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-8 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {accessSolutions.map((solution, index) => <div key={solution.title} data-index={index} className="flex flex-col p-4 rounded-lg bg-card border border-border/80 shadow-xs">
                    <div className="flex items-center justify-center size-9 rounded-md bg-secondary text-secondary-foreground mb-3">
                      {solution.iconName === "Wind" && <Wind className="size-5" />}
                      {solution.iconName === "Layers" && <Layers className="size-5" />}
                      {solution.iconName === "Wrench" && <Wrench className="size-5" />}
                    </div>
                    <h3 className="font-semibold text-sm text-foreground mb-1">
                      {solution.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {solution.description}
                    </p>
                  </div>)}
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="font-semibold">
                <Link to="/ContactRequestQuotePage">
                  Request a Quote
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border">
                <Link to="/SpecialistAccessScaffoldingPage">
                  View Specialist Access
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Card / Visual Column */}
          <motion.div className="lg:col-span-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants}>
            <motion.div variants={itemVariants}>
              <Card className="border border-border shadow-md bg-card overflow-hidden">
                <div className="relative h-56 sm:h-64 w-full overflow-hidden bg-muted">
                  <PostsProvider wp_query={{
                  post_type: "site_photo",
                  per_page: 50,
                  status: ["publish"]
                }} className="w-full h-full">
                    <HvacSitePhoto />
                  </PostsProvider>
                  <div className="absolute inset-0 bg-secondary/15 mix-blend-multiply pointer-events-none" />
                  <div className="absolute top-3 left-3 bg-secondary/90 text-secondary-foreground text-xs font-medium px-2.5 py-1 rounded">
                    Commercial & Industrial Access
                  </div>
                </div>

                <CardHeader className="pb-3 pt-5">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                    <Building2 className="size-4" />
                    <span>BMF Scaffolding Ltd</span>
                  </div>
                  <CardTitle className="text-lg">Reliable site standards & coverage</CardTitle>
                  <CardDescription className="text-xs">
                    Tailored access structures planned around operational building requirements.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {standardAssurances.map((item, index) => <div key={item} data-index={index} className="flex items-start gap-2.5 text-xs text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>)}
                  </div>

                  <Separator className="my-2" />

                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                      <MapPin className="size-3.5 text-muted-foreground" />
                      <span>Regional coverage:</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {serviceAreas.map((area, index) => <span key={area} data-index={index} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-muted text-muted-foreground">
                          {area}
                        </span>)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}